// AddSimulatorModal.js - Modal for adding a new simulator resource
import React from 'react';
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Input,
	Select,
	SelectItem,
	Checkbox,
	addToast
} from '@heroui/react';
import { Monitor } from 'lucide-react';
import SimulatorsApi from '../../api/simulatorsApi.js';
import LocationsApi from '../../api/locationsApi.js';

function AddSimulatorModal({
	isOpen,
	onClose,
	payStatuses = [],
	onSuccess
}) {
	const [saving, setSaving] = React.useState(false);
	const [formData, setFormData] = React.useState({
		name: '',
		regNumb: '',
		locationId: '',
		payStatus: '',
		rate: '',
		enableHobbs: false,
		hobbsValue: '',
		enableRate: false,
		hobbsRate: ''
	});
	const [formErrors, setFormErrors] = React.useState({});

	// Locations from Symfony API
	const [apiLocations, setApiLocations] = React.useState([]);
	const [locationsLoading, setLocationsLoading] = React.useState(false);

	// Fetch locations from Symfony API when modal opens
	React.useEffect(() => {
		if (isOpen) {
			// Reset form
			setFormData({
				name: '',
				regNumb: '',
				locationId: '',
				payStatus: '',
				rate: '',
				enableHobbs: false,
				hobbsValue: '',
				enableRate: false,
				hobbsRate: ''
			});
			setFormErrors({});

			// Fetch locations from API
			const fetchLocations = async () => {
				setLocationsLoading(true);
				try {
					const response = await LocationsApi.getLocations();
					if (response.data?.success && response.data?.data) {
						setApiLocations(response.data.data);
					} else if (Array.isArray(response.data)) {
						setApiLocations(response.data);
					}
				} catch (error) {
					console.error('Error fetching locations:', error);
					setApiLocations([]);
				} finally {
					setLocationsLoading(false);
				}
			};
			fetchLocations();
		}
	}, [isOpen]);

	// Handle form input changes
	const handleInputChange = (field, value) => {
		setFormData(prev => {
			const newData = { ...prev, [field]: value };
			// Clear rate if no charge type selected
			if (field === 'payStatus' && !value) {
				newData.rate = '';
			}
			// Reset hobbs value and rate when disabling
			if (field === 'enableHobbs' && !value) {
				newData.hobbsValue = '0';
				newData.enableRate = false;
				newData.hobbsRate = '';
			}
			if (field === 'enableRate' && !value) {
				newData.hobbsRate = '';
			}
			return newData;
		});
		if (formErrors[field]) {
			setFormErrors(prev => ({ ...prev, [field]: null }));
		}
	};

	// Form validation
	const validateForm = () => {
		const errors = {};
		if (!formData.name.trim()) {
			errors.name = 'Simulator name is required';
		}
		if (!formData.locationId) {
			errors.locationId = 'Location is required';
		}
		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	// Handle form submission
	const handleSubmit = async () => {
		if (!validateForm()) return;

		setSaving(true);

		try {
			// Build simulator data for API
			const simulatorData = {
				name: formData.name.trim(),
				regNumb: formData.regNumb.trim() || null,
				locationId: parseInt(formData.locationId, 10),
				payStatus: formData.payStatus || null,
				rate: formData.rate ? parseFloat(formData.rate) : null,
				enableHobbs: formData.enableHobbs,
				hobbsValue: formData.enableHobbs && formData.hobbsValue ? parseFloat(formData.hobbsValue) : 0,
				enableRate: formData.enableRate,
				hobbsRate: formData.enableRate && formData.hobbsRate ? formData.hobbsRate : null
			};

			console.log('AddSimulatorModal - sending data:', JSON.stringify(simulatorData, null, 2));

			const response = await SimulatorsApi.addSimulator(simulatorData);

			addToast({
				title: 'Success',
				description: `Simulator "${formData.name}" added successfully`,
				color: 'success'
			});

			onClose();
			if (onSuccess) {
				onSuccess(response.data);
			}
		} catch (error) {
			console.error('Error adding simulator:', error);
			const errorMessage =
				error.response?.data?.message ||
				error.response?.data?.error ||
				error.message ||
				'Failed to add simulator';
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
			<ModalContent>
				<ModalHeader className="flex items-center gap-2">
					<Monitor className="w-5 h-5" />
					Add Simulator
				</ModalHeader>
				<ModalBody>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* Left Column - Basic Info */}
						<div className="space-y-4">
							<h4 className="text-sm font-semibold text-default-700">Simulator Information</h4>

							{/* Simulator Name */}
							<Input
								label="Simulator Name"
								placeholder="Enter simulator name"
								variant="flat"
								value={formData.name}
								onValueChange={value => handleInputChange('name', value)}
								isInvalid={!!formErrors.name}
								errorMessage={formErrors.name}
								isRequired
								maxLength={100}
							/>

							{/* Registration Number */}
							<Input
								label="Registration Number"
								placeholder="Enter registration number"
								variant="flat"
								value={formData.regNumb}
								onValueChange={value => handleInputChange('regNumb', value)}
								maxLength={100}
							/>

							{/* Location Select */}
							<Select
								label="Location"
								placeholder={locationsLoading ? 'Loading locations...' : 'Select location'}
								variant="flat"
								selectedKeys={formData.locationId ? [formData.locationId] : []}
								onSelectionChange={keys => handleInputChange('locationId', Array.from(keys)[0])}
								isInvalid={!!formErrors.locationId}
								errorMessage={formErrors.locationId}
								isRequired
								isLoading={locationsLoading}
								isDisabled={locationsLoading}
							>
								{apiLocations.map(location => (
									<SelectItem key={String(location.id)}>
										{location.city ? `${location.name}, ${location.city}` : location.name}
									</SelectItem>
								))}
							</Select>

							{/* Charge Type Select */}
							<Select
								label="Charge Type"
								placeholder="Select charge type"
								variant="flat"
								selectedKeys={formData.payStatus ? [formData.payStatus] : ['none']}
								onSelectionChange={keys => {
									const value = Array.from(keys)[0];
									handleInputChange('payStatus', value === 'none' ? '' : value);
								}}
							>
								<SelectItem key="none">Not Applicable</SelectItem>
								{payStatuses.map(status => (
									<SelectItem key={status.value}>{status.name}</SelectItem>
								))}
							</Select>

							{/* Rate Input */}
							<Input
								label="Rate"
								placeholder="Enter rate"
								variant="flat"
								type="number"
								step="0.01"
								min="0"
								startContent="$"
								value={formData.rate}
								onValueChange={value => handleInputChange('rate', value)}
								isDisabled={!formData.payStatus}
								maxLength={7}
							/>
						</div>

						{/* Right Column - Simulator Meters */}
						<div className="space-y-3">
							<h4 className="text-sm font-semibold text-default-700">Simulator Meters</h4>

							{/* Hobbs Meter Section */}
							<div className="p-3 bg-default-100 rounded-lg space-y-3">
								{/* Enable Hobbs */}
								<div className="flex items-center justify-between">
									<Checkbox
										isSelected={formData.enableHobbs}
										onValueChange={value => handleInputChange('enableHobbs', value)}
										size="sm"
									>
										Enable Hobbs
									</Checkbox>
									<Input
										placeholder="0"
										variant="flat"
										type="text"
										size="sm"
										className="w-24"
										value={formData.hobbsValue}
										onValueChange={value => handleInputChange('hobbsValue', value)}
										isDisabled={!formData.enableHobbs}
										classNames={{ input: 'text-right' }}
									/>
								</div>

								{/* Rate ($)/Hr - independent from Hobbs */}
								<div className="flex items-center justify-between">
									<Checkbox
										isSelected={formData.enableRate}
										onValueChange={value => handleInputChange('enableRate', value)}
										size="sm"
									>
										Rate ($)/Hr
									</Checkbox>
									<Input
										placeholder="0.00"
										variant="flat"
										type="text"
										size="sm"
										className="w-24"
										value={formData.hobbsRate}
										onValueChange={value => handleInputChange('hobbsRate', value)}
										startContent="$"
										isDisabled={!formData.enableRate}
										classNames={{ input: 'text-right' }}
									/>
								</div>
							</div>
						</div>
					</div>
				</ModalBody>
				<ModalFooter>
					<Button color="danger" variant="light" onPress={onClose}>
						Cancel
					</Button>
					<Button
						color="primary"
						onPress={handleSubmit}
						isLoading={saving}
					>
						Add Simulator
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}

export default AddSimulatorModal;
